import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Trophy, BarChart3, Users, Zap, ChevronDown, ChevronUp, Mail, MessageCircle, Share2 } from 'lucide-react';
import { SPORTS_CONFIG } from '../config/sports';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0f2dc054-d2bc-44be-ab56-5351417afe21/artifacts/a2cocwjb_3B8AECB1-0E02-4890-89FD-1DB815461399.png';

const FEATURES = [
  {
    icon: <Trophy className="w-8 h-8" />,
    title: 'Track Every Stat',
    description: 'Tap-to-record stats in real-time during games. Points, rebounds, assists, goals, kills and more.'
  },
  {
    icon: <Star className="w-8 h-8" />,
    title: 'Unlock Achievements',
    description: 'Celebrate milestones with sport-specific badges. Double-doubles, hat tricks, and career records.'
  },
  {
    icon: <Share2 className="w-8 h-8" />,
    title: 'Share with Family',
    description: 'Share game stats with grandparents, coaches, and family via text. Generate beautiful stat cards instantly.'
  },
  {
    icon: <BarChart3 className="w-8 h-8" />,
    title: 'Game History',
    description: 'View complete game logs, star your best performances, and track progress over time.'
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: 'Multi-Sport Athletes',
    description: 'One athlete can play multiple sports. Track basketball in winter, soccer in spring.'
  }
];

const PLANS = [
  {
    name: '2 Sports',
    monthlyPrice: 2.99,
    yearlyPrice: 14.99,
    features: ['Track 2 sports', 'Unlimited games', 'All achievements', 'Game history'],
    popular: false
  },
  {
    name: '4 Sports',
    monthlyPrice: 4.99,
    yearlyPrice: 16.99,
    features: ['Track 4 sports', 'Unlimited games', 'All achievements', 'Game history', 'Priority support'],
    popular: true
  },
  {
    name: 'All Sports',
    monthlyPrice: 6.99,
    yearlyPrice: 19.99,
    features: ['Track all 6 sports', 'Unlimited games', 'All achievements', 'Game history', 'Priority support', 'Early access to new features'],
    popular: false
  }
];

const SPORTS = ['basketball', 'soccer', 'volleyball', 'football', 'baseball', 'softball'];

const FAQS = [
  {
    question: 'How does the free trial work?',
    answer: 'You can track up to 2 games completely free, no credit card required. This lets you experience the full app before committing to a subscription. After your free games, you can upgrade to continue tracking.'
  },
  {
    question: 'Can I track multiple athletes?',
    answer: 'Yes! You can add unlimited athletes to your account. Each athlete can play multiple sports, and all their stats and achievements are tracked separately.'
  },
  {
    question: 'What stats can I track?',
    answer: 'Each sport has tailored stats. Basketball includes points, rebounds, assists, steals, blocks, and more. Soccer tracks goals, assists, shots, and saves. Every sport has 5-15 specific stats relevant to that game.'
  },
  {
    question: 'Can I edit a game after saving it?',
    answer: 'Yes, you can edit any game within 24 hours of creation. After 24 hours, games are locked to preserve data integrity. You\'ll see a countdown timer showing how much time you have left to make edits.'
  },
  {
    question: 'What are achievements?',
    answer: 'Achievements are badges your athlete can unlock by hitting milestones. Examples include scoring 20+ points, getting a hat trick in soccer, or recording a double-double in basketball. They\'re a fun way to celebrate big moments!'
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Absolutely. You can cancel your subscription at any time from your account page. You\'ll continue to have access until the end of your billing period, and your data is always yours.'
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes, we take data security seriously. All data is encrypted in transit and at rest. We never sell your personal information, and you can request data deletion at any time.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 7-day money-back guarantee on all subscriptions. If you\'re not satisfied within the first week, contact support for a full refund.'
  },
  {
    question: 'I\'m having trouble with the app. What should I do?',
    answer: 'First, try refreshing the page or logging out and back in. If the issue persists, clear your browser cache and cookies. For persistent problems, contact our support team at support@inthestands.app with a description of the issue and any error messages you see.'
  },
  {
    question: 'I have a feature suggestion. How can I share it?',
    answer: 'We love hearing from our users! Send your feature suggestions and feedback to support@inthestands.app. We review every suggestion and use your feedback to improve In The Stands. Many of our best features came from user ideas!'
  }
];

export default function Landing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#000]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#000]/90 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <img src={LOGO_URL} alt="In The Stands" className="h-10 w-auto" />
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register')}
              data-testid="nav-signup-btn"
              className="px-4 py-2 bg-[#00E5E5] text-black font-semibold rounded-lg hover:bg-[#5FFBFB] transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <img src={LOGO_URL} alt="In The Stands" className="w-72 h-auto mx-auto mb-8" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Track Your Athlete's Stats<br />
            <span className="text-[#00E5E5]">Like a Pro</span>
          </h1>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            The easiest way to record game stats for youth athletes. Support 6 sports, 
            track achievements, and watch your athlete grow.
          </p>
          
          {/* Sport Icons */}
          <div className="flex justify-center gap-4 mb-10">
            {SPORTS.map(sport => (
              <div
                key={sport}
                className="w-14 h-14 rounded-full bg-[#1A1A1A] flex items-center justify-center text-2xl hover:bg-[#252525] transition-colors"
                title={SPORTS_CONFIG[sport]?.name}
              >
                {SPORTS_CONFIG[sport]?.emoji}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              data-testid="hero-signup-btn"
              className="px-8 py-4 bg-[#00E5E5] text-black font-bold text-lg rounded-xl hover:bg-[#5FFBFB] transition-all transform hover:scale-105"
            >
              Start Free Trial
            </button>
            <button
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-[#1A1A1A] text-white font-semibold text-lg rounded-xl hover:bg-[#252525] transition-colors"
            >
              View Pricing
            </button>
          </div>

          <p className="mt-4 text-sm text-gray-500">
            <Zap className="inline w-4 h-4 mr-1 text-[#00E5E5]" />
            Start with 2 free game entries. No credit card required.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Everything You Need to Track Stats
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
            Built by sports parents, for sports parents. Simple enough to use during a game.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {FEATURES.map((feature, index) => (
              <div
                key={index}
                className="bg-[#1A1A1A] rounded-2xl p-6 hover:bg-[#252525] transition-colors"
              >
                <div className="text-[#00E5E5] mb-4">{feature.icon}</div>
                <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            6 Sports, One App
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Comprehensive stat tracking tailored to each sport
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {SPORTS.map(sport => {
              const config = SPORTS_CONFIG[sport];
              return (
                <div
                  key={sport}
                  className="bg-[#1A1A1A] rounded-2xl p-6 text-center hover:bg-[#00E5E5]/10 hover:border-[#00E5E5] border-2 border-transparent transition-all"
                >
                  <span className="text-5xl block mb-3">{config?.emoji}</span>
                  <h3 className="text-white font-semibold">{config?.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {config?.stats?.length || 0}+ stats
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            How It Works
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Add Your Athlete', desc: 'Create a profile and select their sports' },
              { step: '2', title: 'Track During Games', desc: 'Tap buttons to record stats in real-time' },
              { step: '3', title: 'Watch Them Grow', desc: 'Review history, unlock achievements, celebrate wins' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#00E5E5] text-black text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            See It In Action
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Simple, intuitive interface designed for game-day use
          </p>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Athletes Screenshot Mockup */}
            <div className="text-center">
              <div className="bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] rounded-[40px] p-3 inline-block mx-auto shadow-2xl shadow-[#00E5E5]/20 border border-[#333]">
                <div className="bg-[#000] rounded-[32px] overflow-hidden w-[280px]">
                  {/* Phone Header */}
                  <div className="bg-[#000] px-4 py-2 flex items-center justify-between">
                    <img src={LOGO_URL} alt="Logo" className="h-6" />
                    <div className="flex gap-2 text-gray-400">
                      <span className="text-sm">👤</span>
                      <span className="text-sm">→</span>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="px-4 py-3">
                    <h3 className="text-white font-bold text-lg mb-4">My Athletes</h3>
                    {/* Athlete Card */}
                    <div className="bg-[#1A1A1A] rounded-xl p-3 flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-[#00E5E5] flex items-center justify-center">
                        <span className="text-black text-xl">👤</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Michael Jordan Jr</p>
                        <p className="text-lg">🏀 ⚽</p>
                      </div>
                      <span className="text-gray-500">›</span>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-xl p-3 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#00E5E5] flex items-center justify-center">
                        <span className="text-black text-xl">👤</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold">Sarah Williams</p>
                        <p className="text-lg">🏐 🥎</p>
                      </div>
                      <span className="text-gray-500">›</span>
                    </div>
                  </div>
                  {/* Bottom Button */}
                  <div className="px-4 pb-4">
                    <div className="bg-[#00E5E5] text-black font-bold py-3 rounded-xl text-center text-sm">
                      + Add Athlete
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-lg mt-6">Manage Your Athletes</h3>
              <p className="text-gray-400 text-sm mt-2">
                Add multiple athletes, each with their own sports and stats
              </p>
            </div>

            {/* Game Tracking Screenshot Mockup */}
            <div className="text-center">
              <div className="bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] rounded-[40px] p-3 inline-block mx-auto shadow-2xl shadow-[#00E5E5]/20 border border-[#333]">
                <div className="bg-[#000] rounded-[32px] overflow-hidden w-[280px]">
                  {/* Phone Header */}
                  <div className="bg-[#000] px-4 py-2 flex items-center justify-between">
                    <span className="text-white text-sm">‹ Back</span>
                    <span className="text-white font-semibold">🏀 Basketball</span>
                    <span className="w-12"></span>
                  </div>
                  {/* Score Display */}
                  <div className="bg-[#1A1A1A] mx-4 rounded-xl p-4 mt-2">
                    <p className="text-[#00E5E5] text-5xl font-black text-center">19</p>
                    <p className="text-white text-center font-bold tracking-widest text-sm">PTS</p>
                    <div className="flex justify-center gap-4 mt-3 text-center">
                      <div><p className="text-[#FF9500] font-bold">5</p><p className="text-gray-500 text-xs">2PT</p></div>
                      <div><p className="text-[#FFDD00] font-bold">3</p><p className="text-gray-500 text-xs">3PT</p></div>
                      <div><p className="text-[#5FFBFB] font-bold">4</p><p className="text-gray-500 text-xs">REB</p></div>
                      <div><p className="text-[#BB86FC] font-bold">2</p><p className="text-gray-500 text-xs">AST</p></div>
                    </div>
                  </div>
                  {/* Stat Buttons */}
                  <div className="px-4 py-3 grid grid-cols-3 gap-2">
                    <div className="bg-[#1A1A1A] rounded-lg p-2 text-center border border-[#333]">
                      <p className="text-[#00E5E5] text-xs font-bold">FREE THROW</p>
                    </div>
                    <div className="bg-[#FF9500]/20 rounded-lg p-2 text-center border border-[#FF9500] relative">
                      <span className="absolute -top-1 -right-1 bg-[#FF9500] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">5</span>
                      <p className="text-[#FF9500] text-xs font-bold">2 POINT</p>
                    </div>
                    <div className="bg-[#FFDD00]/20 rounded-lg p-2 text-center border border-[#FFDD00] relative">
                      <span className="absolute -top-1 -right-1 bg-[#FFDD00] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">3</span>
                      <p className="text-[#FFDD00] text-xs font-bold">3 POINT</p>
                    </div>
                    <div className="bg-[#5FFBFB]/20 rounded-lg p-2 text-center border border-[#5FFBFB] relative">
                      <span className="absolute -top-1 -right-1 bg-[#5FFBFB] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">4</span>
                      <p className="text-[#5FFBFB] text-xs font-bold">REBOUND</p>
                    </div>
                    <div className="bg-[#BB86FC]/20 rounded-lg p-2 text-center border border-[#BB86FC] relative">
                      <span className="absolute -top-1 -right-1 bg-[#BB86FC] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">2</span>
                      <p className="text-[#BB86FC] text-xs font-bold">ASSIST</p>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-2 text-center border border-[#333]">
                      <p className="text-[#4ADE80] text-xs font-bold">STEAL</p>
                    </div>
                  </div>
                  {/* Save Button */}
                  <div className="px-4 pb-4">
                    <div className="bg-[#00E5E5] text-black font-bold py-3 rounded-xl text-center text-sm">
                      💾 SAVE GAME
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-lg mt-6">Track Stats Live</h3>
              <p className="text-gray-400 text-sm mt-2">
                Tap to record - points update automatically in real-time
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Simple, Affordable Pricing
          </h2>
          <p className="text-gray-400 text-center mb-8">
            Start free, upgrade when you're ready
          </p>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="bg-[#1A1A1A] rounded-xl p-1 inline-flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  billingCycle === 'monthly' ? 'bg-[#00E5E5] text-black' : 'text-gray-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  billingCycle === 'yearly' ? 'bg-[#00E5E5] text-black' : 'text-gray-400'
                }`}
              >
                Yearly <span className="text-xs ml-1">Save 40%</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-6 relative ${
                  plan.popular
                    ? 'bg-gradient-to-b from-[#00E5E5]/20 to-[#1A1A1A] border-2 border-[#00E5E5]'
                    : 'bg-[#1A1A1A]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5E5] text-black text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#00E5E5]">
                    ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                  </span>
                  <span className="text-gray-400">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-[#00E5E5]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/register')}
                  className={`w-full py-3 rounded-xl font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-[#00E5E5] text-black hover:bg-[#5FFBFB]'
                      : 'bg-[#333] text-white hover:bg-[#444]'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            All plans include a 2-game free trial. Cancel anytime.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Everything you need to know about In The Stands
          </p>

          <div className="space-y-3">
            {FAQS.map((faq, index) => (
              <div
                key={index}
                className="bg-[#1A1A1A] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="text-white font-semibold pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#00E5E5] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-400 mb-4">Still have questions?</p>
            <a
              href="mailto:support@inthestands.app"
              className="inline-flex items-center gap-2 text-[#00E5E5] hover:underline"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#000]">
        <div className="max-w-3xl mx-auto text-center">
          <img src={LOGO_URL} alt="In The Stands" className="w-48 h-auto mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Tracking?
          </h2>
          <p className="text-gray-400 mb-8">
            Join thousands of sports parents who track their athlete's journey with In The Stands.
          </p>
          <button
            onClick={() => navigate('/register')}
            data-testid="cta-signup-btn"
            className="px-10 py-4 bg-[#00E5E5] text-black font-bold text-lg rounded-xl hover:bg-[#5FFBFB] transition-all transform hover:scale-105"
          >
            Create Free Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          {/* Footer Top */}
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <img src={LOGO_URL} alt="In The Stands" className="h-10 w-auto mb-4" />
              <p className="text-gray-500 text-sm">
                The #1 app for tracking youth athlete stats across 6 sports.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white">Pricing</button></li>
                <li><button onClick={() => navigate('/register')} className="text-gray-400 hover:text-white">Get Started</button></li>
                <li><button onClick={() => navigate('/login')} className="text-gray-400 hover:text-white">Sign In</button></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@inthestands.app" className="text-gray-400 hover:text-white flex items-center gap-2"><Mail className="w-4 h-4" /> support@inthestands.app</a></li>
                <li><button onClick={() => setOpenFaq(0)} className="text-gray-400 hover:text-white flex items-center gap-2"><MessageCircle className="w-4 h-4" /> FAQs</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setShowPrivacy(true)} className="text-gray-400 hover:text-white">Privacy Policy</button></li>
                <li><button onClick={() => setShowTerms(true)} className="text-gray-400 hover:text-white">Terms of Service</button></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="pt-8 border-t border-[#1A1A1A] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2026 In The Stands. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <button onClick={() => setShowPrivacy(true)} className="hover:text-white">Privacy</button>
              <button onClick={() => setShowTerms(true)} className="hover:text-white">Terms</button>
              <a href="mailto:support@inthestands.app" className="hover:text-white">Support</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
              <button onClick={() => setShowPrivacy(false)} className="text-gray-400 hover:text-white text-2xl">&times;</button>
            </div>
            <div className="prose prose-invert text-gray-300 space-y-4">
              <p className="text-sm text-gray-500">Last updated: February 2026</p>
              
              <h3 className="text-white font-semibold text-lg">1. Information We Collect</h3>
              <p>We collect information you provide directly, including your email address, athlete names, and game statistics. We also automatically collect usage data such as app interactions and device information.</p>
              
              <h3 className="text-white font-semibold text-lg">2. How We Use Your Information</h3>
              <p>We use your information to provide and improve our services, process transactions, send notifications about your account, and communicate updates about In The Stands.</p>
              
              <h3 className="text-white font-semibold text-lg">3. Marketing Communications</h3>
              <p>If you opt in during registration, we may send you promotional emails about updates, special offers, tips, and new features. You can unsubscribe from marketing emails at any time by clicking the unsubscribe link in any email or contacting support@inthestands.app. Opting out of marketing emails will not affect transactional emails (account confirmations, password resets, subscription updates).</p>
              
              <h3 className="text-white font-semibold text-lg">4. Data Security</h3>
              <p>We implement industry-standard security measures to protect your data. All data is encrypted in transit using TLS and at rest. We regularly audit our security practices.</p>
              
              <h3 className="text-white font-semibold text-lg">5. Data Sharing</h3>
              <p>We do not sell your personal information. We may share data with service providers who assist in operating our platform (payment processing, hosting) under strict confidentiality agreements.</p>
              
              <h3 className="text-white font-semibold text-lg">6. Your Rights</h3>
              <p>You have the right to access, correct, or delete your personal data at any time. You can export your data or request complete deletion by contacting support@inthestands.app. You may also request to opt out of marketing communications at any time.</p>
              
              <h3 className="text-white font-semibold text-lg">7. Children's Privacy</h3>
              <p>Our service is designed for parents and coaches to track youth athletes. We do not knowingly collect information directly from children under 13. All accounts must be created by adults.</p>
              
              <h3 className="text-white font-semibold text-lg">8. Cookies</h3>
              <p>We use essential cookies to maintain your session and preferences. We do not use third-party tracking cookies for advertising purposes.</p>
              
              <h3 className="text-white font-semibold text-lg">9. Data Retention</h3>
              <p>We retain your data for as long as your account is active or as needed to provide services. If you delete your account, we will delete your personal data within 30 days, except where retention is required by law.</p>
              
              <h3 className="text-white font-semibold text-lg">10. Contact Us</h3>
              <p>For privacy-related questions or to exercise your data rights, contact us at support@inthestands.app.</p>
            </div>
            <button
              onClick={() => setShowPrivacy(false)}
              className="mt-6 w-full bg-[#00E5E5] text-black font-bold py-3 rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
              <button onClick={() => setShowTerms(false)} className="text-gray-400 hover:text-white text-2xl">&times;</button>
            </div>
            <div className="prose prose-invert text-gray-300 space-y-4">
              <p className="text-sm text-gray-500">Last updated: February 2026</p>
              
              <h3 className="text-white font-semibold text-lg">1. Acceptance of Terms</h3>
              <p>By accessing or using In The Stands, you agree to be bound by these Terms of Service. If you do not agree, please do not use our service.</p>
              
              <h3 className="text-white font-semibold text-lg">2. Description of Service</h3>
              <p>In The Stands is a web application that allows users to track athletic statistics for youth athletes across multiple sports. Features include stat tracking, achievements, game history, and performance trends.</p>
              
              <h3 className="text-white font-semibold text-lg">3. User Accounts</h3>
              <p>You must create an account to use our service. You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</p>
              
              <h3 className="text-white font-semibold text-lg">4. Subscription and Payments</h3>
              <p>Some features require a paid subscription. Subscriptions are billed monthly or annually as selected. You may cancel at any time; access continues until the end of your billing period.</p>
              
              <h3 className="text-white font-semibold text-lg">5. Free Trial</h3>
              <p>New users receive 2 free game entries. No credit card is required for the free trial. After free games are used, a subscription is required to continue tracking.</p>
              
              <h3 className="text-white font-semibold text-lg">6. Refund Policy</h3>
              <p>We offer a 7-day money-back guarantee on all new subscriptions. Contact support within 7 days of purchase for a full refund. Refunds are processed within 5-10 business days.</p>
              
              <h3 className="text-white font-semibold text-lg">7. Marketing Communications</h3>
              <p>During registration, you may opt in to receive marketing emails including updates, special offers, and tips. This is optional and you can unsubscribe at any time. See our Privacy Policy for details.</p>
              
              <h3 className="text-white font-semibold text-lg">8. User Content</h3>
              <p>You retain ownership of all data you enter (athlete information, game statistics). By using our service, you grant us license to store and process this data to provide the service.</p>
              
              <h3 className="text-white font-semibold text-lg">9. Prohibited Uses</h3>
              <p>You may not use the service for any unlawful purpose, attempt to gain unauthorized access, or interfere with other users' enjoyment of the service.</p>
              
              <h3 className="text-white font-semibold text-lg">10. Limitation of Liability</h3>
              <p>In The Stands is provided "as is" without warranties. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
              
              <h3 className="text-white font-semibold text-lg">11. Changes to Terms</h3>
              <p>We may update these terms from time to time. Continued use after changes constitutes acceptance of the new terms.</p>
              
              <h3 className="text-white font-semibold text-lg">12. Contact</h3>
              <p>Questions about these terms? Contact us at support@inthestands.app.</p>
            </div>
            <button
              onClick={() => setShowTerms(false)}
              className="mt-6 w-full bg-[#00E5E5] text-black font-bold py-3 rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
